import { parseEffectiveBriefing, type BrandReviewState, type EffectiveBriefing, type ReviewRounds } from "@talentkontor/domain";

export type Lang = "de" | "en";

/**
 * Freigabe-Link der Marke (/freigabe/:token, 0073): Antwort von brand_review_open bzw. brand_review_preview in den Typ
 * bringen, Texte in der Sprache der Marke (DE/EN), Links in der Caption erkennen. Reine Funktionen, getestet in
 * apps/web/test/marke-freigabe.test.ts. Der Link zeigt nie Beträge, Konditionen oder interne Notizen (das liefert die
 * Datenbank gar nicht erst).
 */

export const TOKEN = /^[A-Za-z0-9_-]{32,200}$/;
export function tokenOk(t: string | undefined | null): t is string {
  return !!t && TOKEN.test(t);
}

export interface ReviewFile {
  id: string;
  name: string;
  mime: string;
  size: number;
}
/** Datei der Content-Ablage im Link (0155): Video, Bild oder PDF mit Prüfsumme; Bytes nur über die Serverfunktion medien */
export interface ReviewMedia {
  id: string;
  name: string;
  mime: string;
  kind: "video" | "image" | "pdf";
  size: number;
  sha256: string | null;
  duration_ms: number | null;
  width: number | null;
  height: number | null;
  poster: boolean;
}
/** Kommentar der Marke an einer Datei (Zeitmarke im Video oder Punkt im Bild) */
export interface ReviewComment {
  id: string;
  media_id: string;
  time_ms: number | null;
  x: number | null;
  y: number | null;
  body: string;
  name: string | null;
  at: string;
}
export interface ReviewDecision {
  status: "approved" | "changes_requested";
  at: string;
  name: string | null;
  comment: string | null;
  round: number | null;
  extra: boolean;
}
export interface ReviewView {
  review_id: string;
  state: BrandReviewState;
  language: Lang;
  expires_at: string;
  agency: { name: string; address: string | null; email: string | null; website: string | null; logo_path?: string | null };
  brand: string | null;
  recipient_name: string | null;
  creator: string;
  deal_title: string;
  item: { label: string; format: string; platform: string; draft_due: string | null; publish_on: string | null } | null;
  month: string | null;
  submission: { version: number; submitted_at: string; caption: string | null; files: ReviewFile[]; media: ReviewMedia[]; team_approved: boolean };
  comments: ReviewComment[];
  content_sha256: string;
  message: string | null;
  briefing: EffectiveBriefing;
  rounds: ReviewRounds | null;
  history: { status: string; at: string; name: string | null; comment: string | null; version: number | null }[];
  decision: ReviewDecision | null;
  team_view: boolean;
}

const s = (v: unknown): string | null => (typeof v === "string" && v.trim() ? v : null);
const n = (v: unknown): number | null => (typeof v === "number" && Number.isFinite(v) ? v : null);

export function parseReviewView(raw: unknown): ReviewView {
  const o = (typeof raw === "string" ? JSON.parse(raw) : raw ?? {}) as Record<string, unknown>;
  const ag = (o.agency ?? {}) as Record<string, unknown>;
  const it = o.item as Record<string, unknown> | null | undefined;
  const sub = (o.submission ?? {}) as Record<string, unknown>;
  const r = o.rounds as Record<string, unknown> | null | undefined;
  const d = o.decision as Record<string, unknown> | null | undefined;
  const state = (["open", "approved", "changes_requested", "withdrawn", "closed", "expired"] as const).find((x) => x === o.state) ?? "closed";
  return {
    review_id: String(o.review_id ?? ""), state, language: o.language === "en" ? "en" : "de", expires_at: String(o.expires_at ?? ""),
    agency: { name: String(ag.name ?? ""), address: s(ag.address), email: s(ag.email), website: s(ag.website), logo_path: s(ag.logo_path) },
    brand: s(o.brand), recipient_name: s(o.recipient_name), creator: String(o.creator ?? ""), deal_title: String(o.deal_title ?? ""),
    item: it ? { label: String(it.label ?? ""), format: String(it.format ?? ""), platform: String(it.platform ?? ""), draft_due: s(it.draft_due), publish_on: s(it.publish_on) } : null,
    month: s(o.month),
    submission: {
      version: n(sub.version) ?? 1, submitted_at: String(sub.submitted_at ?? ""), caption: s(sub.caption),
      files: (Array.isArray(sub.files) ? sub.files : []).map((f) => {
        const x = f as Record<string, unknown>;
        return { id: String(x.id ?? ""), name: String(x.name ?? ""), mime: String(x.mime ?? ""), size: n(x.size) ?? 0 };
      }),
      media: (Array.isArray(sub.media) ? sub.media : []).map((f) => {
        const x = f as Record<string, unknown>;
        const kind = x.kind === "video" || x.kind === "image" || x.kind === "pdf" ? x.kind : "pdf";
        return { id: String(x.id ?? ""), name: String(x.name ?? ""), mime: String(x.mime ?? ""), kind, size: n(x.size) ?? 0, sha256: s(x.sha256),
                 duration_ms: n(x.duration_ms), width: n(x.width), height: n(x.height), poster: x.poster === true };
      }),
      team_approved: sub.team_approved === true,
    },
    comments: parseReviewComments(o.comments),
    content_sha256: String(o.content_sha256 ?? ""), message: s(o.message), briefing: parseEffectiveBriefing(o.briefing),
    rounds: r && typeof r === "object" ? { used: n(r.used) ?? 0, included: n(r.included), next_extra: r.next_extra === true } : null,
    history: (Array.isArray(o.history) ? o.history : []).map((h) => {
      const x = h as Record<string, unknown>;
      return { status: String(x.status ?? ""), at: String(x.at ?? ""), name: s(x.name), comment: s(x.comment), version: n(x.version) };
    }),
    decision: d && (d.status === "approved" || d.status === "changes_requested")
      ? { status: d.status, at: String(d.at ?? ""), name: s(d.name), comment: s(d.comment), round: n(d.round), extra: d.extra === true } : null,
    team_view: o.team_view === true,
  };
}

/** Kommentare der Marke (aus brand_review_open bzw. brand_review_comment) */
export function parseReviewComments(raw: unknown): ReviewComment[] {
  return (Array.isArray(raw) ? raw : []).map((c) => {
    const x = c as Record<string, unknown>;
    return { id: String(x.id ?? ""), media_id: String(x.media_id ?? ""), time_ms: n(x.time_ms), x: n(x.x), y: n(x.y), body: String(x.body ?? ""), name: s(x.name), at: String(x.at ?? "") };
  });
}

/** Caption in Text und Links zerlegen (nur http und https werden zu Links; alles bleibt Text im Dokument). */
export function textParts(text: string): { text: string; href: string | null }[] {
  const out: { text: string; href: string | null }[] = [];
  const re = /https?:\/\/[^\s<>"')]+/gi;
  let last = 0;
  for (const m of text.matchAll(re)) {
    const at = m.index ?? 0;
    if (at > last) out.push({ text: text.slice(last, at), href: null });
    const url = m[0].replace(/[.,;:!?]+$/, "");
    out.push({ text: url, href: url });
    last = at + url.length;
  }
  if (last < text.length) out.push({ text: text.slice(last), href: null });
  return out;
}

export function fileSize(bytes: number, lang: Lang): string {
  const fmt = new Intl.NumberFormat(lang === "en" ? "en-GB" : "de-DE", { maximumFractionDigits: 1 });
  return bytes >= 1024 * 1024 ? `${fmt.format(bytes / 1024 / 1024)} MB` : `${Math.max(1, Math.round(bytes / 1024))} KB`;
}

export function fmtDate(iso: string | null | undefined, lang: Lang, withTime = false): string {
  if (!iso) return "";
  const dateOnly = /^\d{4}-\d{2}-\d{2}$/.test(iso);
  const d = new Date(dateOnly ? `${iso}T12:00:00Z` : iso);
  if (Number.isNaN(d.getTime())) return "";
  return new Intl.DateTimeFormat(lang === "en" ? "en-GB" : "de-DE", withTime && !dateOnly
    ? { day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit" }
    : { day: "2-digit", month: "2-digit", year: "numeric", ...(dateOnly ? { timeZone: "UTC" } : {}) }).format(d);
}

/** Pflichtangaben vor dem Absenden, wie die Datenbank (Name 2 bis 120 Zeichen, Änderung mit Beschreibung, Zusatzschleife bestätigt). */
export function decisionProblem(input: { approve: boolean; name: string; comment: string; extraAck: boolean }, rounds: ReviewRounds | null, lang: Lang): string | null {
  const name = input.name.trim();
  if (name.length < 2 || name.length > 120) return lang === "en" ? "Please enter your name." : "Bitte geben Sie Ihren Namen an.";
  if (input.comment.trim().length > 4000) return lang === "en" ? "The comment may have at most 4000 characters." : "Der Kommentar darf höchstens 4000 Zeichen haben.";
  if (!input.approve) {
    if (input.comment.trim().length < 3) return lang === "en" ? "Please describe the change you would like." : "Bitte beschreiben Sie die gewünschte Änderung.";
    if (rounds?.next_extra && !input.extraAck) {
      return lang === "en" ? "Please confirm that this additional revision round is charged separately." : "Bitte bestätigen Sie, dass diese zusätzliche Korrekturschleife gesondert berechnet wird.";
    }
  }
  return null;
}

export const T = {
  de: {
    skip: "Zum Inhalt", theme: "Hell / Dunkel", switchLang: "English", loading: "Wird geladen …",
    eyebrow: (a: string) => `${a} bittet um Freigabe`, title: (l: string) => `Freigabe: ${l}`, draftTitle: "Freigabe eines Entwurfs",
    sub: (c: string, d: string, b: string | null) => [c, d, b ? `für ${b}` : null].filter(Boolean).join(" · "),
    validUntil: "Link gültig bis", draftDue: "Entwurf bis", postOn: "Veröffentlichung geplant am",
    message: (a: string) => `Nachricht von ${a}`, draft: "Entwurf", version: (v: number, d: string) => `Version ${v} · eingereicht am ${d}`,
    files: "Dateien", open: "Ansehen", download: "Herunterladen", noFiles: "Der Entwurf besteht aus Text oder einem Link.",
    decide: "Ihre Entscheidung", name: "Ihr Name", namePh: "Vor- und Nachname", comment: "Kommentar", commentApprove: "Kommentar (optional)",
    commentPh: "Was soll geändert werden? Je genauer, desto schneller kommt die nächste Fassung.",
    approve: "Freigeben", changes: "Änderung wünschen", extraAck: "Mir ist bewusst, dass das eine zusätzliche Korrekturschleife ist, die gesondert berechnet wird.",
    evidence: "Öffnen und Entscheidung werden mit Zeitpunkt, IP-Adresse, Browser und einem Prüfwert der gezeigten Fassung protokolliert: Nachweis für Sie und die Agentur.",
    doneApproved: "Vielen Dank, der Entwurf ist freigegeben.", doneChanges: "Vielen Dank, Ihr Änderungswunsch ist angekommen.",
    doneText: (a: string) => `${a} kümmert sich um alles Weitere. Sie können diese Seite schließen.`,
    decided: (st: string, name: string | null, d: string) => `${st === "approved" ? "Freigegeben" : "Änderung gewünscht"}${name ? ` von ${name}` : ""} am ${d}.`,
    history: "Bisherige Rückmeldungen", historyRow: (v: number | null, st: string) => `${v ? `Version ${v}: ` : ""}${st === "approved" ? "freigegeben" : "Änderung gewünscht"}`,
    stateClosed: "Die Agentur hat diese Fassung bereits abschließend bearbeitet. Eine Entscheidung ist hier nicht mehr nötig.",
    stateExpired: "Dieser Link ist abgelaufen. Bitte fordern Sie bei Ihrer Kontaktperson einen neuen Link an.",
    teamView: "Vorschau: So sieht die Marke den Link. Entscheidungen sind hier nicht möglich; ein Aufruf zählt nicht.",
    fileError: "Die Datei ließ sich nicht laden. Bitte versuchen Sie es erneut.", ref: "Prüfwert der Fassung",
    contact: "Fragen?", privacy: "Datenschutz",
  },
  en: {
    skip: "Skip to content", theme: "Light / Dark", switchLang: "Deutsch", loading: "Loading …",
    eyebrow: (a: string) => `${a} requests your approval`, title: (l: string) => `Approval: ${l}`, draftTitle: "Draft approval",
    sub: (c: string, d: string, b: string | null) => [c, d, b ? `for ${b}` : null].filter(Boolean).join(" · "),
    validUntil: "Link valid until", draftDue: "Draft due", postOn: "Planned publication",
    message: (a: string) => `Message from ${a}`, draft: "Draft", version: (v: number, d: string) => `Version ${v} · submitted on ${d}`,
    files: "Files", open: "View", download: "Download", noFiles: "The draft consists of text or a link.",
    decide: "Your decision", name: "Your name", namePh: "First and last name", comment: "Comment", commentApprove: "Comment (optional)",
    commentPh: "What should be changed? The more precise, the faster the next version.",
    approve: "Approve", changes: "Request changes", extraAck: "I understand that this is an additional revision round that is charged separately.",
    evidence: "Opening and the decision are logged with time, IP address, browser and a checksum of the version shown: proof for you and the agency.",
    doneApproved: "Thank you, the draft is approved.", doneChanges: "Thank you, your change request has been received.",
    doneText: (a: string) => `${a} will take care of the rest. You can close this page.`,
    decided: (st: string, name: string | null, d: string) => `${st === "approved" ? "Approved" : "Changes requested"}${name ? ` by ${name}` : ""} on ${d}.`,
    history: "Previous feedback", historyRow: (v: number | null, st: string) => `${v ? `Version ${v}: ` : ""}${st === "approved" ? "approved" : "changes requested"}`,
    stateClosed: "The agency has already completed this version. No decision is needed here any more.",
    stateExpired: "This link has expired. Please ask your contact person for a new link.",
    teamView: "Preview: this is what the brand sees. Decisions are not possible here; viewing does not count.",
    fileError: "The file could not be loaded. Please try again.", ref: "Checksum of the version",
    contact: "Questions?", privacy: "Privacy",
  },
} as const;
